import * as React from "react";
import { Box, Flex, Spacer, Text, IconButton } from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { ObjectiveListModal } from "../ObjectiveListModal";
import { useTranslation } from "react-i18next";
import { useStores } from "../../hooks/useStores";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { GenericObjective } from "../../types";
import { Objective } from "./Objective";
import ReactGA from "react-ga";
import ReactPixel from "react-facebook-pixel";

type Props = {
  userId: string;
  boxShadow?: string;
  mt?: string;
  mb?: string;
  isSubscriptionDeactivated?: boolean;
};

export const ObjectiveList: React.FC<Props> = observer(
  ({ userId, boxShadow, mt, mb, isSubscriptionDeactivated = false }) => {
    const { clientsStore } = useStores();
    const { t } = useTranslation();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [objective, setObjective] = useState<GenericObjective | null>(null);

    const { enqueueSnackbar } = useSnackbar();

    const [isEditable, setIsEditable] = React.useState(true);

    useEffect(() => {
      if (userId) {
        clientsStore.getGenericObjective(userId);
      }
    }, [clientsStore, userId]);

    const handleSubmit = useCallback(
      async (data: any) => {
        try {
          if (!objective) {
            await clientsStore.addGenericObjective(userId, data);
            setIsModalVisible(false);
            ReactGA.event({
              category: "Customer",
              action: "Setup Obiettivi",
            });
            ReactPixel.track("Setup Obiettivi");
          } else {
            await clientsStore.updateGenericObjective(userId, data);
            setIsModalVisible(false);
            ReactGA.event({
              category: "Customer",
              action: "Setup Obiettivi",
            });
            ReactPixel.track("Setup Obiettivi");
          }
          setObjective(null);
          setIsEditable(true);
        } catch (err) {
          console.log(err);
          enqueueSnackbar(t("screens.client.emailForm.submitError"), {
            variant: "error",
          });
        }
      },
      [clientsStore, enqueueSnackbar, objective, t, userId]
    );

    const onCheck = useCallback(
      async (genericObjectiveId: GenericObjective["id"]) => {
        try {
          let newData = {
            id: genericObjectiveId,
            checked: true,
          };
          await clientsStore.updateGenericObjective(userId, newData);
          ReactGA.event({
            category: "Customer",
            action: "Setup Obiettivi",
          });
          ReactPixel.track("Setup Obiettivi");
          setIsModalVisible(false);
        } catch (err) {
          console.log(err);
          enqueueSnackbar(t("screens.client.emailForm.submitError"), {
            variant: "error",
          });
        }
      },
      [clientsStore, enqueueSnackbar, t, userId]
    );

    const onEdit = (data: GenericObjective) => {
      setIsEditable(true);
      setObjective(data);
      setIsModalVisible(true);
    };

    const onShow = (data: GenericObjective) => {
      setIsEditable(false);
      setObjective(data);
      setIsModalVisible(true);
    };

    return (
      <>
        <Box mt={mt} mb={mb}>
          <Flex>
            <Box pl="4" alignSelf="center">
              <Text fontSize="20px">
                {t("screens.client.genericObjective.title")}
              </Text>
            </Box>
            <Spacer />
            <Box pr="4">
              <IconButton
                aria-label="Search database"
                variant="ghost"
                colorScheme="black"
                fontSize="20px"
                icon={<AddIcon />}
                onClick={() => {
                  setIsEditable(true);
                  setIsModalVisible(true);
                }}
                disabled={isSubscriptionDeactivated}
              />
            </Box>
          </Flex>

          <Flex flexDirection="column">
            {clientsStore.genericObjectives
              .filter((genericObjective) => !genericObjective.checked)
              .map((item) => (
                <Objective
                  userId={userId}
                  data={item}
                  onCheck={onCheck}
                  onEdit={onEdit}
                  onShow={onShow}
                  isSubscriptionDeactivated={isSubscriptionDeactivated}
                />
              ))}
          </Flex>
        </Box>
        <ObjectiveListModal
          genericObjective={objective}
          isOpen={isModalVisible}
          onClose={() => {
            setIsModalVisible(false);
            setObjective(null);
          }}
          onSubmit={handleSubmit}
          editable={isEditable}
        />
      </>
    );
  }
);
